import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const STANDARD = ['480p', '720p', '1080p'];

const normalize = (r) => r.trim().toLowerCase().replace(/\s+/g, '');

export const ResolutionPricingEditor = ({ rows, defaultResolution, onChange }) => {
  const [custom, setCustom] = useState('');
  const byRes = Object.fromEntries(rows.map((r) => [r.resolution, r]));
  const all = [...STANDARD, ...rows.map((r) => r.resolution).filter((r) => !STANDARD.includes(r))];

  const emit = (next, def = defaultResolution) => {
    const enabled = new Set(next.map((r) => r.resolution));
    onChange(next, enabled.has(def) ? def : (next[0]?.resolution || '720p'));
  };
  const toggle = (res) =>
    emit(byRes[res] ? rows.filter((r) => r.resolution !== res) : [...rows, { resolution: res, credits_per_second: '' }]);
  const setPrice = (res, v) => emit(rows.map((r) => (r.resolution === res ? { ...r, credits_per_second: v } : r)));
  const addCustom = () => {
    const res = normalize(custom);
    if (!res || byRes[res]) return;
    emit([...rows, { resolution: res, credits_per_second: '' }]);
    setCustom('');
  };

  return (
    <div className="space-y-2" data-testid="resolution-pricing-editor">
      <Label>Resolutions &amp; credits per second</Label>
      <div className="rounded-md border divide-y">
        {all.map((res) => {
          const row = byRes[res];
          return (
            <div key={res} className="flex items-center gap-3 px-3 py-2">
              <input type="checkbox" checked={!!row} onChange={() => toggle(res)}
                data-testid={`resolution-enable-${res}`} className="h-4 w-4" />
              <span className="w-16 font-medium">{res}</span>
              <Input type="number" min="0" step="0.01" disabled={!row} placeholder="credits / second"
                value={row?.credits_per_second ?? ''} onChange={(e) => setPrice(res, e.target.value)}
                data-testid={`resolution-price-${res}`} className="max-w-[180px]" />
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input type="radio" name="default-resolution" disabled={!row} checked={defaultResolution === res}
                  onChange={() => onChange(rows, res)} data-testid={`resolution-default-${res}`} />
                Default
              </label>
              {!STANDARD.includes(res) && (
                <button type="button" onClick={() => toggle(res)} className="ml-auto text-gray-400 hover:text-red-500"
                  data-testid={`resolution-remove-${res}`}><X className="w-4 h-4" /></button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom, e.g. 768p, 2k, 4k"
          className="max-w-[220px]" data-testid="resolution-custom-input"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
        <Button type="button" variant="outline" onClick={addCustom} data-testid="resolution-custom-add">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      <p className="text-xs text-gray-500">
        Charged = seconds of video (rounded up) × the price of the requested resolution. Unlisted or missing
        resolution uses the Default row; if nothing is priced, 50/second.
      </p>
    </div>
  );
};
